/**
 * `/studio/clients/$slug` — one client's whole page.
 *
 * The slug is validated in the route rather than in the page: `ClientSlugSchema` is
 * what the API resolves an action's client with, so an address that could never name
 * a client is a not-found rather than a page that renders a refusal where an account
 * should be. `?section=` and `?period=` are the tabs and the month.
 *
 * The **path** is a not-found and the **search** is a fallback, and the difference is
 * the whole distinction: a slug that names nothing has no page to show, while a tab
 * that names nothing has five to choose from and a default among them.
 *
 * ## A tab's own label is an address for it
 *
 * Session 24 found this page twice by guessing: `?section=work-orders` for `work`,
 * and `?section=invoices` for `money`. Both silently rendered the Overview and then
 * **rewrote the address to say so**, which is the worst answer of the three available
 * — a reader who typed the obvious word came away believing the Invoices tab shows
 * the Overview. The fallback is right for a value that names nothing; these two name
 * something, and what they name is the tab's own printed label.
 *
 * So the label is an alias for the key, derived from `SECTIONS` rather than written
 * out: "Work orders" is `work-orders` and "Invoices" is `invoices`, and a tab renamed
 * tomorrow brings its alias with it. Aliasing here rather than in the schema is
 * deliberate — `ClientDetailSearchSchema` is the set of tabs this page has, which is
 * seven, and an alias is a fact about addresses. The router still rewrites, so
 * `?section=invoices` lands on Invoices and the address settles on `?section=money`:
 * one page per address, and the address the reader is left with is the canonical one.
 */

import { isRef } from '@fareast/shared'
import { createFileRoute, notFound } from '@tanstack/react-router'

import { NotFound } from '../../../errors/NotFound.js'
import {
  ClientDetailPage,
  ClientDetailSearchSchema,
  SECTIONS,
} from '../../../pages/studio/clientDetail/ClientDetailPage.js'
import { routeSearch } from '../../../search/useSearchState.js'

/** Each tab's printed label, slugged, pointing at the key the schema knows. */
const SECTION_ALIASES: ReadonlyMap<string, string> = new Map(
  SECTIONS.map((section) => [section.label.toLowerCase().replace(/\s+/g, '-'), section.key]),
)

/**
 * The address with a label swapped for its key, and everything else untouched.
 *
 * Only a **string** that is not already a key is looked up: an alias may never
 * shadow a real tab, and a value the map does not know is left exactly as it was so
 * the schema still gets to refuse it and fall back.
 */
function withSectionAlias<T>(raw: T): T {
  const asked = (raw as Record<string, unknown> | null)?.['section']
  if (typeof asked !== 'string') return raw
  const key = SECTION_ALIASES.get(asked)
  if (key === undefined || key === asked) return raw
  return { ...(raw as object), section: key } as T
}

const parseSearch = routeSearch(ClientDetailSearchSchema)

/**
 * `routeSearch`'s answer, over the aliased address, **keeping `.schema`** — which is
 * what `routes.test.tsx` enumerates every declaring route by. A bare function here
 * would silently drop this route out of that list.
 */
const validateSearch: typeof parseSearch = Object.assign(
  (raw: Parameters<typeof parseSearch>[0]) => parseSearch(withSectionAlias(raw)),
  { schema: parseSearch.schema },
)

export const Route = createFileRoute('/studio/clients/$slug')({
  beforeLoad: ({ params }) => {
    if (!isRef('client', params.slug)) throw notFound()
  },
  validateSearch,
  /** R3-13: the boundary is this route, so the studio shell outlives the refusal. */
  notFoundComponent: NotFound,
  component: ClientDetailRoute,
})

function ClientDetailRoute() {
  const { slug } = Route.useParams()
  return <ClientDetailPage slug={slug} />
}
